import React, { useCallback, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SpinnerOverlay } from '@components/common';
import AIAssistant from '@components/common/AIAssistant';
import ErrorPage from '@components/common/ErrorPage';
import Header from '@components/common/Header';
import SnackbarsContainer from '@components/common/SnackbarsContainer';
import AdminPanel from '@components/routes/AdminPanel';
import AppMapContainer from '@components/routes/AppMapContainer';
import UserProfile from '@components/routes/UserProfile';

import { useQueryKaiAssistantByUser } from '@hooks/kaiAssistants/useQueryKaiAssistantByUser';
import { useRedirectOnOrgSwitch } from '@hooks/routes/useRedirectOnOrgSwitch';
import { useQueryUser } from '@hooks/useQueryUser';

import { auth, common } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setUpdatedUserRoles } from '@slices/auth';
import { setIsKaiAssistantLoaded, setIsMapMode } from '@slices/common';

import { isEmpty } from '@utils/common';
import { checkGroupPanelAccess } from '@utils/permissions';

import { EUserRoles } from '@enums/common';

import { STORAGE_KEYS } from '@constants/common';
import {
	adminGroupAccess,
	cmModuleGroupAccess,
	mapGroupAccess,
	userProfileGroupAccess,
} from '@constants/menuGroupAccess';
import { ROUTE_NAMES } from '@constants/routeNames';
import { routes } from '@constants/routes';
import { EErrorPageCodeKey } from '@components/common/ErrorPage/constants';

const {
	adminPanel,
	map,
	userProfile,
} = routes;

const Skeleton = () => {
	const dispatch = useAppDispatch();
	const {
		accessPermission,
		user,
	} = useAppSelector(auth);
	const { user: userObj, isLoadingCurrentUser } = useQueryUser();
	const { pathname } = useLocation();

	const isKaiAssistantActive = userObj?.organization?.isKaiAssistantsEnabled;

	const { kaiAssistant } = useQueryKaiAssistantByUser(userObj?.id, isKaiAssistantActive);

	const isKaiAssistantLoaded = !!(
		!isEmpty(kaiAssistant) &&
		kaiAssistant?.isEnabled &&
		isKaiAssistantActive
	);

	const listOfRoles = useMemo(() => userObj?.roles?.map(item => item.name) || [], [userObj]);

	const { snackbars } = useAppSelector(common);

	const fetchedPermissions = Object.keys(EUserRoles).reduce<Record<keyof any, boolean>>((acc, role) => {
		acc = { ...acc, [role]: listOfRoles.includes((EUserRoles as any)[role]) };

		return acc;
	}, {} as Record<keyof any, boolean>);

	const isPlatformOwner = listOfRoles.length === 0 && user?.roles?.length !== 0;
	const permissions = isPlatformOwner ? accessPermission : fetchedPermissions;

	const isRolesExists = isPlatformOwner ? !!user?.roles : !!listOfRoles.length;

	const isMapAccess = checkGroupPanelAccess(permissions, mapGroupAccess);
	const isVisibleCmModule = checkGroupPanelAccess(permissions, cmModuleGroupAccess);
	const isVisibleAdminPanel = checkGroupPanelAccess(permissions, adminGroupAccess);
	const isVisibleMap = checkGroupPanelAccess(permissions, mapGroupAccess);
	const isUserProfile = checkGroupPanelAccess(permissions, userProfileGroupAccess);
	const isTopLevelReady  = isRolesExists && !!userObj && !isLoadingCurrentUser;
	const isOnMap = pathname.startsWith(map.index);
	const isOnAdmin = pathname.startsWith(adminPanel.index);
	const orgSwitchActive = !!sessionStorage.getItem(STORAGE_KEYS.ORG_SWITCH_RELOAD_FLAG);
	const isOnUser = pathname.startsWith(userProfile.index);

	const isAllowedHereTop = (
		(isOnMap && isVisibleMap) ||
		(isOnAdmin && isVisibleAdminPanel) ||
		(isOnUser  && isUserProfile) ||
		(!isOnMap && !isOnAdmin && !isOnUser)
	);

	useRedirectOnOrgSwitch({
		isReady: isTopLevelReady,
		isAllowedHere: isAllowedHereTop,
		isGateReady: !(isOnAdmin && orgSwitchActive),
		isConsumeFlagWhenAllowed: false,
	});

	const manageRootRouteRedirect = useCallback(() => {
		if (!isRolesExists || !userObj || isLoadingCurrentUser) {
			return;
		}

		if (isVisibleMap) {
			return (
				<Navigate
					replace
					to={map.index}
				/>
			);
		}

		if (!isVisibleMap && isVisibleAdminPanel) {
			return (
				<Navigate
					replace
					to={adminPanel.index}
				/>
			);
		}

		if (!isVisibleCmModule && !isVisibleAdminPanel && !isVisibleMap) {
			return (
				<Navigate
					replace
					to={userProfile.index}
				/>
			);
		}

		return <Navigate
			replace
			to={map.index}
		/>;
	}, [
		isVisibleMap,
		isVisibleAdminPanel,
		isVisibleCmModule,
		userObj,
		isLoadingCurrentUser,
		isRolesExists,
	]);

	useEffect(() => {
		dispatch(setIsKaiAssistantLoaded(isKaiAssistantLoaded));
	}, [dispatch, isKaiAssistantLoaded]);

	useEffect(() => {
		dispatch(setIsMapMode(pathname === ROUTE_NAMES.MAP && isMapAccess));
	}, [pathname, isMapAccess, dispatch]);

	useEffect(() => {
		if (listOfRoles.length) {
			dispatch(setUpdatedUserRoles(listOfRoles));
		}
	}, [listOfRoles, dispatch]);

	return (
		<>
			<div className='main-wrapper'>
				<SnackbarsContainer snackbars={snackbars} />
				<Header />
				<div className='main-content'>
					{!isRolesExists
						? <SpinnerOverlay />
						: <Routes>
							<Route
								element={isVisibleMap
									? <AppMapContainer />
									: <ErrorPage />
								}
								path={map.index}
							/>
							<Route
								element={isVisibleAdminPanel
									? <AdminPanel />
									: <ErrorPage />
								}
								path={adminPanel.index + '/*'}
							/>
							<Route
								element={isUserProfile
									? <UserProfile />
									: <ErrorPage />
								}
								path={userProfile.index}
							/>
							<Route
								element={manageRootRouteRedirect()}
								path='/'
							/>
							<Route
								element={<ErrorPage errorKey={EErrorPageCodeKey.notFound} />}
								path='*'
							/>
						</Routes>
					}
				</div>
			</div>
			{isKaiAssistantActive && kaiAssistant && <AIAssistant kaiAssistant={kaiAssistant} />}
		</>
	);
};

export default React.memo(Skeleton);
